import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './PaymentSuccess.css';

const PACKAGE_UPFRONT = { trial: 70, bronze: 100, silver: 150, gold: 200 };
const PACKAGE_TOTAL = { trial: 70, bronze: 360, silver: 710, gold: 1050 };

const formatGBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP'
});

export default function PaymentSuccess() {
  const location = useLocation();
  const [status, setStatus] = useState('verifying'); // verifying | success | failed
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setStatus('failed');
      return;
    }

    fetch(`/api/payments/verify-session?session_id=${encodeURIComponent(sessionId)}`)
      .then(r => r.json())
      .then(data => {
        if (data.paid) {
          setBooking(data);
          setStatus('success');
          localStorage.removeItem('pendingBooking');
        } else {
          setStatus('failed');
        }
      })
      .catch(() => setStatus('failed'));
  }, [location.search]);

  // ── Verifying ──
  if (status === 'verifying') {
    return (
      <div className="ps-page">
        <div className="ps-hero">
          <div className="ps-hero-inner">
            <div className="ps-check" style={{ opacity: 0.6 }}>⏳</div>
            <h1>Verifying payment…</h1>
            <p>Please wait while we confirm your payment with Stripe.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Failed / Not Paid ──
  if (status === 'failed') {
    return (
      <div className="ps-page">
        <div
          className="ps-hero"
          style={{ background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)' }}
        >
          <div className="ps-hero-inner">
            <div className="ps-check">
              <svg viewBox="0 0 52 52" fill="none">
                <circle cx="26" cy="26" r="26" fill="rgba(255,255,255,0.15)" />
                <path
                  d="M16 16l20 20M36 16L16 36"
                  stroke="#fff"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h1>Payment Not Confirmed</h1>
            <p>
              We could not verify your payment. If you believe this is an error,
              please contact us.
            </p>
          </div>
        </div>

        <div className="ps-body">
          <div className="ps-card">
            <p
              style={{
                color: '#64748b',
                textAlign: 'center',
                marginBottom: 24
              }}
            >
              Your payment was either cancelled or did not complete successfully.
              No booking has been created.
            </p>

            <div className="ps-actions">
              <Link to="/book-lesson" className="ps-btn ps-btn--primary">
                Try Again
              </Link>

              <Link to="/" className="ps-btn ps-btn--outline">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ──
  const pkgKey = booking?.package_type || 'trial';
  const upfront = booking?.upfront ?? PACKAGE_UPFRONT[pkgKey] ?? 70;
  const total = PACKAGE_TOTAL[pkgKey] ?? upfront;

  return (
    <div className="ps-page">

      {/* Hero Banner */}
      <div className="ps-hero">
        <div className="ps-hero-inner">
          <div className="ps-check">
            <svg viewBox="0 0 52 52" fill="none">
              <circle cx="26" cy="26" r="26" fill="rgba(255,255,255,0.15)" />
              <path
                d="M14 27l9 9 15-16"
                stroke="#fff"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h1>Payment Successful!</h1>

          <p>
            Thank you for booking with <strong>Fast Track Driving School</strong>.
            <br className="ps-br" />
            Your payment has been received — we'll be in touch shortly.
          </p>
        </div>
      </div>

      {/* Main Card */}
      <div className="ps-body">
        <div className="ps-card">

          {/* Booking Summary */}
          <div className="ps-summary">
            <h2 className="ps-section-title">
              <span className="ps-title-bar" />
              Booking Summary
            </h2>

            <div className="ps-rows">

              {booking?.full_name && (
                <div className="ps-row">
                  <span>Name</span>
                  <strong>{booking.full_name}</strong>
                </div>
              )}

              {booking?.email && (
                <div className="ps-row">
                  <span>Email</span>
                  <strong>{booking.email}</strong>
                </div>
              )}

              {booking?.phone && (
                <div className="ps-row">
                  <span>Phone</span>
                  <strong>{booking.phone}</strong>
                </div>
              )}

              {booking?.test_centre && (
                <div className="ps-row">
                  <span>Test Centre</span>
                  <strong>{booking.test_centre}</strong>
                </div>
              )}

              {booking?.transmission && (
                <div className="ps-row">
                  <span>Transmission</span>
                  <strong>{booking.transmission}</strong>
                </div>
              )}

              {booking?.pkg_label && (
                <div className="ps-row">
                  <span>Package</span>
                  <strong>{booking.pkg_label}</strong>
                </div>
              )}

              <div className="ps-row ps-row--total">
                <span>Paid Upfront (Online)</span>
                <strong className="ps-price">
                  {formatGBP.format(upfront)}
                </strong>
              </div>

              {total > upfront && (
                <div
                  className="ps-row"
                  style={{ fontSize: '0.87rem', color: '#64748b' }}
                >
                  <span>Remaining (paid to instructor)</span>
                  <strong>{formatGBP.format(total - upfront)}</strong>
                </div>
              )}

            </div>
          </div>

          {/* Next Steps */}
          <div className="ps-next">
            <h2 className="ps-section-title">
              <span className="ps-title-bar" />
              What Happens Next?
            </h2>

            <div className="ps-steps">

              <div className="ps-step">
                <div className="ps-step-icon">📧</div>
                <div>
                  <strong>Confirmation Email</strong>
                  <p>Check your inbox for a booking confirmation. If you don't see it, check your <strong>spam or junk folder</strong>.</p>
                </div>
              </div>

              <div className="ps-step">
                <div className="ps-step-icon">📞</div>
                <div>
                  <strong>Instructor Call</strong>
                  <p>Your assigned instructor will call to arrange the lesson time.</p>
                </div>
              </div>

              <div className="ps-step">
                <div className="ps-step-icon">🚗</div>
                <div>
                  <strong>Enjoy Your Lesson!</strong>
                  <p>Get ready to start your driving journey with us.</p>
                </div>
              </div>

            </div>
          </div>

          <div className="ps-actions">
            <Link to="/" className="ps-btn ps-btn--outline">
              ← Back to Home
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}